/**
 * sessionResume — "after 15 minutes away, reopening the app lands on My Day."
 *
 * The last tab is still restored for a quick re-open (founder report
 * 2026-06-22: "once the app gets closed it loses the last page"), but once the
 * app has been backgrounded/closed for 15+ minutes, the next foreground resets
 * to My Day — a returning user shouldn't be dropped back into a stale deep view
 * (a half-finished workout screen, someone's Trybe feed) they opened an hour
 * ago. Works on iOS/Android (Capacitor appStateChange) and web
 * (visibilitychange); wired up in MainApp.
 */

export const RESUME_RESET_MS = 15 * 60 * 1000; // 15 minutes
const LAST_ACTIVE_KEY = "trybeup_last_active_ts";

/** Stamp "the app was in use just now". Called on foreground and background. */
export function markActive(now: number = Date.now()): void {
  try {
    localStorage.setItem(LAST_ACTIVE_KEY, String(now));
  } catch {
    /* localStorage unavailable (private mode / quota) — feature simply no-ops */
  }
}

/**
 * True if the app has been away (backgrounded/closed) for at least `resetMs`.
 * Returns false when there's no prior record (first ever launch) so a brand-new
 * user isn't "reset" on their very first open.
 */
export function isStaleSession(now: number = Date.now(), resetMs: number = RESUME_RESET_MS): boolean {
  try {
    const raw = localStorage.getItem(LAST_ACTIVE_KEY);
    if (!raw) return false;
    const last = Number(raw);
    if (!Number.isFinite(last) || last <= 0) return false;
    return now - last >= resetMs;
  } catch {
    return false;
  }
}

/**
 * Decide which tab a cold launch lands on. Encapsulates the "first time or
 * after 15 min away → My Day" rule plus the shared-device guard: a persisted
 * tab is only restored for the SAME user who owns it. A different or first-time
 * user (owner mismatch) always starts on My Day — the persisted tab is not
 * user-specific, so on a shared browser it must never leak across accounts
 * (founder report: a newly-registered user opened on Habits, not My Day).
 */
export function resolveLandingTab(opts: {
  saved: string | null;
  owner: string | null;
  userId: string | null | undefined;
  stale: boolean;
  isValid: (tab: string) => boolean;
}): string {
  const { saved, owner, userId, stale, isValid } = opts;
  if (stale) return "myday";
  if (!saved || !isValid(saved)) return "myday";
  if (owner && userId && owner !== userId) return "myday";
  return saved;
}

// A tapped push notification navigates to its own target (a duel, a Trybe, a
// habit reminder → My Day). The resume-reset must never override that, so
// push.ts stamps this on every notification click and the reset skips if a push
// nav happened moments ago.
let _lastPushNavTs = 0;
export function markPushNav(now: number = Date.now()): void {
  _lastPushNavTs = now;
}
export function pushNavWithin(ms: number, now: number = Date.now()): boolean {
  return _lastPushNavTs > 0 && now - _lastPushNavTs <= ms;
}
